import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  filter,
  forkJoin,
  map,
  of,
  retryWhen,
  switchMap,
  tap,
  timer,
} from 'rxjs';

import {
  Account,
  AccountDTO,
  AccountMetricsDTO,
  AccountMetricsResponse,
  AccountTariff,
  AccountsResponse,
} from '@personal-area/global/accounts/models';
import { AuthService } from '@personal-area/global/authorization/services';
import { retryStrategy } from '@personal-area/global/http/services';
import { Role } from '@personal-area/global/users/models';
import { UserService } from '@personal-area/global/users/services';

import { AccountService } from '../../services/account.service';
import { calculateBalanceFixed } from '../../utils/balance-utils';
import { AccountActions } from '../actions/account';

const PAUSE_BETWEEN_CREATE_AND_METRICS = 1000;

const combineAccountWithBalance = (
  account: AccountDTO,
  metrics: AccountMetricsDTO,
): Account => ({
  ...account,
  ...metrics,
  daysLeft: 0,
  balanceFixed: calculateBalanceFixed(
    account.type,
    metrics.balance,
    metrics.credit,
    metrics.equity,
  ),
});

@Injectable()
export class AccountEffects {
  constructor(
    private _actions$: Actions,
    private _authService: AuthService,
    private _accountService: AccountService,
    private _userService: UserService,
  ) {}

  getAccounts$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AccountActions.getAccounts),
      switchMap(() =>
        this._accountService.fetchAccounts().pipe(retryWhen(retryStrategy())),
      ),
      switchMap((accounts) =>
        accounts.list.length
          ? this._accountService
              .fetchMetrics(
                this._authService.userId,
                accounts.list.map(({ id }) => id),
              )
              .pipe(
                retryWhen(retryStrategy()),
                map((metrics) => ({ accounts, metrics })),
              )
          : of({
              accounts: <AccountsResponse>{
                list: [],
                primaryAccountId: 0,
              },
              metrics: <AccountMetricsResponse>{
                balances: {},
              },
            }),
      ),
      tap(({ accounts: { list } }) => {
        if (
          !this._authService.authToken?.usedAccountTypes.includes(
            Role.Exchanger,
          ) &&
          list.some((acc) => acc.tariff === AccountTariff.exchanger)
        ) {
          this._userService.addRoleToAccountTypes(Role.Exchanger);
        }

        if (
          !this._authService.authToken?.usedAccountTypes.includes(
            Role.Partner,
          ) &&
          list.some((acc) => acc.tariff === AccountTariff.partner)
        ) {
          this._userService.addRoleToAccountTypes(Role.Partner);
        }
      }),
      map(({ accounts, metrics: { balances } }) =>
        AccountActions.getAccountsSuccess(
          accounts.list
            .filter(({ id }) => id in balances)
            .map((account) =>
              combineAccountWithBalance(account, balances[account.id]),
            ),
          accounts.primaryAccountId,
        ),
      ),
      catchError(() => of(AccountActions.getAccountsFail())),
    ),
  );

  selectPrimary$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AccountActions.selectPrimaryAccount),
        filter((id) => !!id && this._authService.isLoggedIn),
        switchMap(({ accountId }) =>
          this._accountService.setPrimaryAccount(
            this._authService.userId,
            accountId,
          ),
        ),
      ),
    { dispatch: false },
  );

  getAccount$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AccountActions.getAccount),
      filter(({ accountId }) => !!accountId && this._authService.isLoggedIn),
      switchMap(({ accountId }) =>
        forkJoin([
          this._accountService
            .fetchAccount(accountId)
            .pipe(retryWhen(retryStrategy())),
          timer(PAUSE_BETWEEN_CREATE_AND_METRICS).pipe(
            switchMap(() =>
              this._accountService.fetchMetrics(this._authService.userId, [
                accountId,
              ]),
            ),
            retryWhen(retryStrategy()),
          ),
        ]),
      ),
      map(([account, { balances }]) =>
        !(account.id in balances)
          ? AccountActions.getAccountFail()
          : AccountActions.getAccountSuccess({
              account: combineAccountWithBalance(account, balances[account.id]),
            }),
      ),
      catchError(() => of(AccountActions.getAccountFail())),
    ),
  );

  createPartnerAccount$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AccountActions.createPartnerAccount),
      switchMap(() =>
        this._accountService.openPartnerAccount().pipe(
          tap(() => this._userService.addRoleToAccountTypes(Role.Partner)),
          map(() => AccountActions.createPartnerAccountSuccess()),
        ),
      ),
    ),
  );
}
